import React from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import WebvonkLogoImage from '../../assets/Webvonk.png';
import { useTheme } from '../../contexts/ThemeContext';

const pulse = keyframes`
  0%, 100% { opacity: 0.2; transform: scale(0.8); }
  50%       { opacity: 1;   transform: scale(1); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// ─── Fullscreen variant ───────────────────────────────────────────────────────

const FullscreenWrap = styled.div`
  position: fixed;
  inset: 0;
  background: ${props => props.theme.colors.background};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 32px;
  z-index: 9999;
  animation: ${fadeIn} 0.25s ease;
`;

const LogoImg = styled.img`
  width: 72px;
  height: 72px;
  object-fit: contain;
  filter: ${props => props.$light ? 'brightness(0)' : 'brightness(0) invert(1)'};
  opacity: 0.9;
`;

// ─── Inline variant ───────────────────────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.$spacing || '40px'};
  gap: 20px;
`;

// ─── Shared ───────────────────────────────────────────────────────────────────

const Dots = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Dot = styled.div`
  width: ${props => props.$size || '8px'};
  height: ${props => props.$size || '8px'};
  border-radius: 50%;
  background: #f97316;
  animation: ${pulse} 1.4s ease-in-out infinite;
  animation-delay: ${props => props.$delay || '0s'};
`;

const LoadingText = styled.div`
  font-size: ${props => props.$fontSize || '14px'};
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  text-align: center;
  opacity: 0.6;
  letter-spacing: -0.01em;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const WebvonkLoader = ({
  text = 'Laden...',
  subText,
  size = 'medium',
  spacing,
  fullscreen = false,
}) => {
  const { themeMode } = useTheme();
  const isLight = themeMode === 'light';
  const dotSize = size === 'small' ? '6px' : size === 'large' ? '10px' : '8px';
  const fontSize = size === 'small' ? '12px' : size === 'large' ? '16px' : '14px';
  const pad = spacing || (size === 'small' ? '20px' : size === 'large' ? '60px' : '40px');

  const dots = (
    <Dots>
      <Dot $size={dotSize} $delay="0s" />
      <Dot $size={dotSize} $delay="0.2s" />
      <Dot $size={dotSize} $delay="0.4s" />
    </Dots>
  );

  if (fullscreen) {
    return ReactDOM.createPortal(
      <FullscreenWrap>
        <LogoImg src={WebvonkLogoImage} alt="Webvonk" $light={isLight} />
        {dots}
        {text && <LoadingText $fontSize="14px">{text}</LoadingText>}
      </FullscreenWrap>,
      document.body
    );
  }

  return (
    <Container $spacing={pad}>
      {dots}
      {text && <LoadingText $fontSize={fontSize}>{text}</LoadingText>}
    </Container>
  );
};

export default WebvonkLoader;
